import { useRef, type RefObject } from "react";

/** Runs `trigger` once; ignores repeats until `event` fires on `ref` (or `fallbackMs`) settles. */
export const useRefEventOnce = <K extends keyof HTMLElementEventMap>(
  ref: RefObject<HTMLElement | null>,
  event: K,
  fallbackMs = 550,
) => {
  const settling = useRef(false);

  return (trigger: () => void) => {
    if (settling.current) return;
    settling.current = true;

    const el = ref.current;
    // assigned once but enclosed in release before assigned
    // biome-ignore lint/style/useConst: <explanation>
    let timeoutId: ReturnType<typeof setTimeout>;
    const release = () => {
      settling.current = false;
      clearTimeout(timeoutId);
      el?.removeEventListener(event, release);
    };

    trigger();

    if (!el) {
      release();
      return;
    }

    el.addEventListener(event, release, { once: true });
    timeoutId = setTimeout(release, fallbackMs);
  };
};

export type RefEventOnce = ReturnType<typeof useRefEventOnce>;
